import { useEffect, useState } from "react";
import PropTypes from "prop-types";
/* Components */
import { Card } from "semantic-ui-react";
import { Row, Col, Button } from "react-bootstrap";
import BlurredNumber from "../CommonForBoth/Blurred/BlurredNumber";
/* Selectors */
import { selectEthCallMultiple, selectLastTransact } from "@ensuro/ethereum-store";
import { selectAPICallMultiple } from "@ensuro/api-calls-store";
import { selectUserAddress } from "@/store/user/selectors";
import { selectCurrent } from "@store/eTokens/selectors";
/* Helpers */
import { makeWeTokenCalls } from "@/helpers/wetoken_helper";
import { oneMinuteTicks } from "@/helpers/store_helper";
/* Modals */
import DepositModal from "../Modal/DepositModal";
import WithdrawModal from "../Modal/WithdrawModal";
import WrapModal from "../Modal/WrapModal";
import UnwrapModal from "../Modal/UnwrapModal";
import TxModal from "../Modal/TxModal";
import ConnectButton from "../CommonForBoth/ConnectButton";
import KycIframeModal from "../Modal/KycIframeModal";
/* i18n and config */
import { withTranslation } from "react-i18next";
import { connect, useDispatch } from "react-redux";
import { api, currency, ethereum, pool } from "@/config";
import "./single_eToken.scss";

const ETH_CLOCK_1MIN = oneMinuteTicks(ethereum.clock);
// +2 ticks: api-calls-store caches every successful call for a hardcoded 60s, and the clock's API_CALL
// drops any per-call maxAge, so a 60s poll lands inside that cache and never reaches the network.
const API_CLOCK_1MIN = oneMinuteTicks(api.clock) + 2;

const makeEthCalls = function (userAddress, etkAddress) {
  return [
    { address: currency.address, abi: "ERC20", method: "balanceOf", args: [userAddress] },
    { address: etkAddress, abi: "EToken", method: "balanceOf", args: [userAddress] },
  ];
};

// Kept out of this component so the ConnectButton can reuse it without an import cycle.
export { makeWeTokenCalls };

const makeWhitelistCall = (userAddress, etkAddress, etkWhitelist) => {
  return [
    {
      address: etkWhitelist.address,
      abi: "ILPWhitelist",
      method: "acceptsDeposit(address,address,uint256)",
      args: [etkAddress, userAddress, 1],
    },
    {
      address: etkWhitelist.address,
      abi: "ILPWhitelist",
      method: "acceptsWithdrawal(address,address,uint256)",
      args: [etkAddress, userAddress, 1],
    },
  ];
};

const makeAPICalls = (userAddress) => {
  return [{ apiName: "lpsDeposits", args: [userAddress] }];
};

const getTxOriginAction = (tx, etkAddress, { wrapperAddress, poolAddress } = {}) => {
  if (!tx) return null;
  if (tx.method === "approve") {
    if (tx.address === etkAddress) return "wrap";
    if (tx.address === currency.address) return "deposit";
    return null;
  }
  if (tx.method === "deposit" || tx.method === "depositWithPermit") {
    if (tx.address === wrapperAddress) return "wrap";
    if (tx.address === poolAddress) return "deposit";
    return null;
  }
  if (tx.method === "withdraw") {
    if (tx.address === etkAddress) return "withdraw";
    if (tx.address === wrapperAddress) return "unwrap";
    return null;
  }
  return null;
};

const txKeyOf = (tx) => (tx ? tx.hash || tx.txHash || tx.transactionHash : null);

const UserInformation = ({
  t,
  user,
  userAddress,
  etkAddress,
  etkSymbol,
  usdcBalance,
  etkBalance,
  lastTx,
  acceptsDeposit,
  acceptsWithdrawal,
  etkWhitelist,
  lpsDeposits,
  wrapper,
  wrapperAddress,
  poolAddress,
  weTokenBalance,
  weTokenRate,
}) => {
  const [flow, setFlow] = useState(null);
  const [wlModalVisible, showWLModal] = useState(false);
  let dispatch = useDispatch();

  const inAction = flow?.actionOpen === true && flow?.txOpen !== true;
  const depositModal = flow?.action === "deposit" && inAction;
  const withdrawModal = flow?.action === "withdraw" && inAction;
  const wrapModal = flow?.action === "wrap" && inAction;
  const unwrapModal = flow?.action === "unwrap" && inAction;
  const txModal = flow?.txOpen === true;
  const setActionFlow = (action) =>
    setFlow({ action, actionOpen: true, txOpen: false, openedAfterApprove: false, shownTxKey: null });

  const lpWhitelistedOut = acceptsDeposit?.value === false && acceptsWithdrawal?.value === false;

  useEffect(() => {
    if (!lastTx) return;
    const isMined = lastTx.state === "MINED";
    if (!isMined || !txKeyOf(lastTx)) return;

    if (!flow || flow.txOpen !== true) return;
    const originAction = getTxOriginAction(lastTx, etkAddress, { wrapperAddress, poolAddress });
    if (flow.action !== originAction) return;

    setFlow({
      action: flow.action,
      actionOpen: true,
      txOpen: false,
      openedAfterApprove: lastTx.method === "approve",
      shownTxKey: flow.shownTxKey,
    });
  }, [lastTx, etkAddress, flow, wrapperAddress, poolAddress]);

  // Ethereum and API useEffects
  useEffect(() => {
    if (userAddress) {
      dispatch({
        type: "ETH_ADD_SUBSCRIPTION",
        key: "userInfo",
        componentEthCalls: makeEthCalls(userAddress, etkAddress),
        clockCount: ETH_CLOCK_1MIN,
      });
      if (etkWhitelist !== null) {
        dispatch({
          type: "ETH_ADD_SUBSCRIPTION",
          key: "userWhitelist",
          componentEthCalls: makeWhitelistCall(userAddress, etkAddress, etkWhitelist),
          clockCount: ETH_CLOCK_1MIN,
        });
      }
    }
    return () => {
      dispatch({ type: "ETH_REMOVE_SUBSCRIPTION", key: "userInfo" });
      dispatch({ type: "ETH_REMOVE_SUBSCRIPTION", key: "userWhitelist" });
    };
  }, [dispatch, etkAddress, etkWhitelist, userAddress]);

  useEffect(() => {
    if (!userAddress || lpWhitelistedOut) return;
    dispatch({
      type: "API_ADD_SUBSCRIPTION",
      key: "userInfo",
      componentApiCalls: makeAPICalls(userAddress),
      clockCount: API_CLOCK_1MIN,
    });
    return () => dispatch({ type: "API_REMOVE_SUBSCRIPTION", key: "userInfo" });
  }, [dispatch, userAddress, lpWhitelistedOut]);

  useEffect(() => {
    if (!userAddress || !wrapperAddress) return;
    dispatch({
      type: "ETH_ADD_SUBSCRIPTION",
      key: "userInfoWeToken",
      componentEthCalls: makeWeTokenCalls(wrapper, userAddress),
      clockCount: ETH_CLOCK_1MIN,
    });
    return () => {
      dispatch({ type: "ETH_REMOVE_SUBSCRIPTION", key: "userInfoWeToken" });
    };
  }, [dispatch, wrapper, wrapperAddress, userAddress]);

  useEffect(() => {
    if (lastTx?.state !== "MINED" || !userAddress) return;
    [...makeEthCalls(userAddress, etkAddress), ...makeWeTokenCalls(wrapper, userAddress)].forEach((call) =>
      dispatch({ type: "ETH_CALL", ...call, forceCall: true })
    );
    if (!lpWhitelistedOut) {
      makeAPICalls(userAddress).forEach((call) => dispatch({ type: "API_CALL", ...call, forceCall: true }));
    }
  }, [dispatch, lastTx, userAddress, etkAddress, wrapper, lpWhitelistedOut]);

  useEffect(() => {
    if (!flow || flow.actionOpen !== true || flow.txOpen) return;
    if (!lastTx || lastTx.state !== "QUEUED") return;
    const txKey = txKeyOf(lastTx);
    if (flow.shownTxKey === txKey) return;
    const originAction = getTxOriginAction(lastTx, etkAddress, { wrapperAddress, poolAddress });
    if (flow.action !== originAction) return;

    setFlow({ ...flow, txOpen: true, shownTxKey: txKey });
  }, [lastTx, etkAddress, flow, wrapperAddress, poolAddress]);

  const canDeposit = acceptsDeposit && acceptsDeposit.value; // acceptsDeposit.value === true ==> Whitelisted

  const canWithdraw = acceptsWithdrawal && acceptsWithdrawal.value; // acceptsWithdrawal.value === true ==> Whitelisted

  let net = { value: 0, state: "LOADED" };
  let earned = {};
  if (lpsDeposits && lpsDeposits.value) {
    let etk = lpsDeposits.value.find((d) => d.e_token.address === etkAddress);
    net = { value: etk ? etk.net_deposits : 0, state: "LOADED" };
  }
  earned = etkBalance.state === "LOADED" ? { value: etkBalance.value - net.value, state: "LOADED" } : {};

  const wrappedEquivalent =
    weTokenBalance?.value && weTokenRate?.value
      ? { value: weTokenBalance.value.mul(weTokenRate.value), state: "LOADED" }
      : {};

  return (
    <>
      <Card fluid className="your-info-padding right-padding">
        <h4>{t("Your Info")}</h4>
        <br />

        {!userAddress ? (
          <>
            <p>{t("PleaseConnectYourWalletTxt")}</p>
            <br />
            <div className="your-info-buttons">
              <ConnectButton user={user} />
            </div>
          </>
        ) : (
          <>
            <Row>
              <Col md={8}>
                <p>{t("Available to Supply")}</p>
              </Col>
              <Col md={4}>
                <p>
                  <BlurredNumber
                    number={usdcBalance}
                    suffix={` ${currency.name}`}
                    options={{ maximumFractionDigits: 2, notation: "standard" }}
                  />
                </p>
              </Col>
            </Row>

            <Row>
              <Col md={8}>
                <p>{t("Your Balance")}</p>
              </Col>
              <Col md={4}>
                <p>
                  <BlurredNumber
                    number={etkBalance}
                    suffix={` ${etkSymbol}`}
                    options={{ maximumFractionDigits: 2, notation: "standard" }}
                  />
                </p>
              </Col>
            </Row>

            {wrapper && weTokenBalance?.value?.gt(0) && (
              <>
                <Row>
                  <Col md={8}>
                    <p>{t("Wrapped Balance")}</p>
                  </Col>
                  <Col md={4}>
                    <p>
                      <BlurredNumber
                        number={weTokenBalance}
                        suffix={` ${wrapper.symbol}`}
                        options={{ maximumFractionDigits: 2, notation: "standard" }}
                      />
                    </p>
                  </Col>
                </Row>

                <Row>
                  <Col md={8} />
                  <Col md={4}>
                    <p>
                      ={" "}
                      <BlurredNumber
                        number={wrappedEquivalent}
                        suffix={` ${etkSymbol}`}
                        options={{ maximumFractionDigits: 2, notation: "standard" }}
                      />
                    </p>
                  </Col>
                </Row>
              </>
            )}

            <Row>
              <Col md={8}>
                <p>{t("Net Deposits")}</p>
              </Col>
              <Col md={4}>
                <p>
                  <BlurredNumber
                    number={net}
                    suffix={` ${etkSymbol}`}
                    options={{ maximumFractionDigits: 2, notation: "standard" }}
                  />
                </p>
              </Col>
            </Row>

            <Row>
              <Col md={8}>
                <p>{t("Earned")}</p>
              </Col>
              <Col md={4}>
                <p>
                  <BlurredNumber
                    number={earned}
                    suffix={` ${etkSymbol}`}
                    options={{ maximumFractionDigits: 2, notation: "standard" }}
                  />
                </p>
              </Col>
            </Row>

            <br />

            <div className="your-info-buttons">
              {wrapper && (
                <>
                  <Button
                    variant="secondary"
                    onClick={() => setActionFlow("wrap")}
                    disabled={etkBalance.value === undefined || etkBalance.value.eq(0)}
                  >
                    {t("Wrap")}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => setActionFlow("unwrap")}
                    disabled={weTokenBalance.value === undefined || weTokenBalance.value.eq(0)}
                  >
                    {t("Unwrap")}
                  </Button>
                </>
              )}
              <Button
                variant="secondary"
                onClick={() => (canDeposit ? setActionFlow("deposit") : showWLModal(true))}
                disabled={acceptsDeposit.value === undefined || usdcBalance.value === undefined}
              >
                {t("Deposit")}
              </Button>
              <Button
                variant="secondary"
                onClick={() => (canWithdraw ? setActionFlow("withdraw") : showWLModal(true))}
                disabled={etkBalance.value === undefined || etkBalance.value.eq(0)}
              >
                {t("Withdraw")}
              </Button>
            </div>
          </>
        )}
      </Card>

      <KycIframeModal mode={"kyc"} show={wlModalVisible} onHide={() => showWLModal(false)} />
      <DepositModal
        show={depositModal}
        onHide={() => setFlow(null)}
        balance={usdcBalance}
        openedAfterApprove={flow?.openedAfterApprove}
      />
      {withdrawModal && <WithdrawModal show={withdrawModal} onHide={() => setFlow(null)} balance={etkBalance} />}
      {wrapper && (
        <WrapModal show={wrapModal} onHide={() => setFlow(null)} openedAfterApprove={flow?.openedAfterApprove} />
      )}
      {wrapper && unwrapModal && <UnwrapModal show={unwrapModal} onHide={() => setFlow(null)} />}
      {lastTx && (lastTx.state === "QUEUED" || lastTx.state === "MINED" || lastTx.state === "EXPIRED") && (
        <TxModal
          show={txModal}
          onHide={() => setFlow({ ...flow, actionOpen: false, txOpen: false })}
          tx={lastTx || {}}
        />
      )}
    </>
  );
};

UserInformation.propTypes = {
  t: PropTypes.any,
};

export const mapStateToProps = (state) => {
  const user = state.UserReducer;
  const userAddress = selectUserAddress(state.UserReducer);
  const etk = selectCurrent(state.ETokensReducer);
  const etkAddress = etk?.address;
  const etkSymbol = etk?.symbol;
  const etkWhitelist = etk && etk.whitelist;
  const wrapper = etk?.custom_data?.wrapper;
  const wrapperAddress = wrapper?.address;
  const poolAddress = pool.address;
  const lastTx = selectLastTransact(state.EthereumReducer);

  const [lpsDeposits] = selectAPICallMultiple(state.APIReducer, makeAPICalls(userAddress));
  const [usdcBalance, etkBalance] = !userAddress
    ? [{}, {}]
    : selectEthCallMultiple(state.EthereumReducer, makeEthCalls(userAddress, etkAddress));
  const [weTokenBalance, weTokenRate] =
    !userAddress || !wrapper?.address
      ? [{}, {}]
      : selectEthCallMultiple(state.EthereumReducer, makeWeTokenCalls(wrapper, userAddress));

  const [acceptsDeposit, acceptsWithdrawal] =
    !userAddress || etkWhitelist === undefined
      ? [{}, {}]
      : etkWhitelist === null
        ? [
            { state: "LOADED", value: true },
            { state: "LOADED", value: true },
          ]
        : selectEthCallMultiple(state.EthereumReducer, makeWhitelistCall(userAddress, etkAddress, etkWhitelist));

  return {
    user,
    userAddress,
    etkAddress,
    etkSymbol,
    usdcBalance,
    etkBalance,
    lastTx,
    acceptsDeposit,
    acceptsWithdrawal,
    etkWhitelist,
    lpsDeposits,
    wrapper,
    wrapperAddress,
    poolAddress,
    weTokenBalance,
    weTokenRate,
  };
};

export default connect(mapStateToProps)(withTranslation()(UserInformation));
